import React, { FC } from 'react'

import { ArtboardDetailInfoFragment } from '@sketch/gql-types'
import { IS_EMBEDDED } from '@sketch/constants'
import { ShareWithoutVersion, useVersioning } from 'modules/versioning'
import {
  DocumentSidebarLayoutHeaderPropsOld as DocumentSidebarLayoutHeaderProps,
  versionedRoutes,
} from '@sketch/modules-common'
import { useForDesktop, useForTablet, Navbar } from '@sketch/components'
import { Zoom, ZoomTypes } from 'components/Zoom'

import { AddAnnotationNavbarItem } from 'modules/annotations/components'
import { usePlaceDraftAnnotation } from 'modules/annotations/hooks'

import HeaderBadge from '../HeaderBadge'
import HeaderNavigation from '../HeaderNavigation'
import HeaderSidebarToggle from '../HeaderSidebarToggle'
import { EditInSketchButton, OpenTemplateButton } from '../EditInSketchButton'
import VersionLinkButton from '../VersionLinkButton'
import PlayPrototypeLink from '../PlayPrototypeLink'
import BackButton, { useBackButton } from '../BackButton'

import {
  NavbarItem,
  NavbarBackButtonItem,
  MiddleContainer,
  AddAnnotationText,
} from './DetailHeader.styles'
import DetailSettingsDropdown from './DetailSettingsDropdown'

type DetailHeaderProps = {
  share: ShareWithoutVersion
  artboard?: ArtboardDetailInfoFragment
  artboardLatestVersion: string
  isViewingLatestVersion: boolean
  isPrototypePlayEnabled: boolean
  userCanOpenInApp: boolean
  zoom: ZoomTypes
  setZoom: (newZoom: ZoomTypes) => void
} & Pick<
  DocumentSidebarLayoutHeaderProps,
  'setSidebarLeftOpen' | 'toggleSidebarRight' | 'isSidebarRightOpen'
>

export const DetailHeader: FC<DetailHeaderProps> = ({
  share,
  artboard,
  artboardLatestVersion,
  setSidebarLeftOpen,
  toggleSidebarRight,
  isSidebarRightOpen,
  isPrototypePlayEnabled,
  isViewingLatestVersion,
  userCanOpenInApp,
  zoom,
  setZoom,
}) => {
  const { workspace, type } = share
  const isDesktop = useForDesktop()
  const isTabletAndBigger = useForTablet()
  const { latestVersionId } = useVersioning()
  const canOpenInApp = userCanOpenInApp && isDesktop && !IS_EMBEDDED
  const { link: shouldRenderBackButton } = useBackButton()

  const [placeDraftAnnotation] = usePlaceDraftAnnotation() || []

  const isTemplate = type === 'TEMPLATE'

  if (placeDraftAnnotation && isTabletAndBigger) {
    return (
      <Navbar>
        <MiddleContainer />
        <AddAnnotationText>Click on the Artboard to comment</AddAnnotationText>
        <MiddleContainer>
          <NavbarItem>
            <Zoom zoom={zoom} setZoom={setZoom} />
          </NavbarItem>
          <AddAnnotationNavbarItem />
        </MiddleContainer>
        <HeaderSidebarToggle
          toggleSidebar={toggleSidebarRight}
          isSidebarOpen={isSidebarRightOpen}
        />
      </Navbar>
    )
  }

  const showLatestVersionButton =
    isTabletAndBigger &&
    !isViewingLatestVersion &&
    !IS_EMBEDDED &&
    // never show if user can only access latest version (it will update automatically)
    share.versionTypeAvailableToUser !== 'LATEST'

  const latestVersionPathname = versionedRoutes.ARTBOARD_DETAIL.VERSION.create({
    permanentArtboardShortId: artboard?.permanentArtboardShortId!,
    shareID: share.identifier,
    versionShortId: latestVersionId,
  })

  return (
    <Navbar>
      <HeaderBadge
        workspace={workspace}
        setSidebarLeftOpen={setSidebarLeftOpen}
      />

      {isTabletAndBigger && shouldRenderBackButton && (
        <NavbarBackButtonItem>
          <BackButton />
        </NavbarBackButtonItem>
      )}

      <HeaderNavigation share={share} artboard={artboard} />

      {isTabletAndBigger && (
        <NavbarItem>
          <Zoom zoom={zoom} setZoom={setZoom} />
        </NavbarItem>
      )}

      {canOpenInApp && isTemplate && (
        <NavbarItem>
          <OpenTemplateButton
            shareId={share.identifier}
            disabled={!isViewingLatestVersion}
          />
        </NavbarItem>
      )}

      {canOpenInApp && !isTemplate && (
        <NavbarItem>
          <EditInSketchButton
            shareId={share.identifier}
            disabled={!isViewingLatestVersion}
          />
        </NavbarItem>
      )}

      {artboard && showLatestVersionButton && (
        <NavbarItem>
          <VersionLinkButton to={latestVersionPathname}>
            See Latest Version
          </VersionLinkButton>
        </NavbarItem>
      )}

      {artboard && isTabletAndBigger && artboard.isFlowHome && (
        <NavbarItem>
          <PlayPrototypeLink
            isPrototypePlayEnabled={isPrototypePlayEnabled}
            permanentArtboardShortId={artboard.permanentArtboardShortId!}
            shareIdentifier={share.identifier}
            representation="navbar"
          />
        </NavbarItem>
      )}

      {isTabletAndBigger && <AddAnnotationNavbarItem />}
      <DetailSettingsDropdown />

      <HeaderSidebarToggle
        toggleSidebar={toggleSidebarRight}
        isSidebarOpen={isSidebarRightOpen}
      />
    </Navbar>
  )
}

export default DetailHeader
